import React, { useCallback, useContext, useEffect, useState } from 'react';
import MTable from '../../../../Components/MTable/MTable';
import { useIntl } from 'react-intl';
import { useAptorApi } from '../../../../Api';
import { MTableToolbar, Query } from '@material-table/core';
import { IGridQueryRequest, OrderDirection } from '../../../../Api/AptorApi';
import { ChangeFilter } from './Filters';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIsMounted } from '../../../../Hooks/useIsMounted';
import moment from 'moment';
import { useFilter } from '../../../../Components/ListFilter';
import { useStyles } from "../../LawPortal.styles";
import { Button, Tooltip, Link as MuiLink } from '@material-ui/core'
import { ArrowUpward, Edit, SaveAlt } from '@material-ui/icons'
import { ModuleContext } from "../../../../Context/ModuleContext/ModuleContext";
import { UserContext } from '../../../../Context/UserContext/UserContext';
import { ChangeAcknowledgeDialog, ChangeAcknowledgeDialogTarget } from '../../Components/ChangeAcknowledgeDialog';

type AcknowledgedChange = {
  id: number;
  tableRowId: string;
  number: string;
  effectiveFrom: Date;
  lawId?: number;
  lawReqId?: number;
  type: 'Law' | 'Requirement';
  name: string;
  userId: number;
  userName: string;
  acknowledgedAt: Date;
  comment: string | null;
  affectsBusiness: boolean | null;
};

export const AcknowledgedChanges = () => {
  const { formatMessage } = useIntl();
  const { api } = useAptorApi();
  const [filters, setFilter] = useFilter();
  const tableRef = React.useRef<any>(null);
  const isMounted = useIsMounted();
  const classes = useStyles();
  const { setHeader } = useContext(ModuleContext);
  const user = useContext(UserContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isFiltersInitialized, setIsFiltersInitialized] = useState(false);
  const [editingAcknowledgement, setEditingAcknowledgement] = useState<
    AcknowledgedChange & { mode: 'review' | 'edit' }
  >();

  const openChangeDialog = (row: AcknowledgedChange, mode: 'review' | 'edit') => {
    setEditingAcknowledgement({ ...row, mode });
  };

  useEffect(() => {
    if (isMounted && isFiltersInitialized) {
      tableRef.current && (tableRef.current as any).onQueryChange();
    }
  }, [filters, tableRef, isMounted, isFiltersInitialized]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const queryParamsMap = {
      companyUnit: 'companyUnit',
      complianceUsers: 'complianceUsers',
      category: 'category',
      area: 'area',
    };
    Object.entries(queryParamsMap).forEach(([queryKey, stateKey]) => {
      const queryValues = searchParams.getAll(queryKey);
      if (queryValues.length) {
        setFilter(stateKey, queryValues.join(',').split(',').map(num => num.toString()));
      }
    });
    setIsFiltersInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // setHeader(formatMessage({ id: 'law-portal.our-laws.header', defaultMessage: 'Our laws' }), [
    //   {
    //     title: formatMessage({ id: 'law-portal.our-law.tab.law-list' }),
    //     to: `/law-portal/our-laws`,
    //   },
    //   {
    //     title: formatMessage({ id: 'law-portal.statistics.sub-menu.changes.unacknowledged' }),
    //     to: `/law-portal/our-laws/unacknowledged`,
    //   },
    //   {
    //     title: formatMessage({ id: 'law-portal.statistics.sub-menu.changes.acknowledged' }),
    //     to: `/law-portal/our-laws/acknowledged`,
    //   },
    // ]);
     setHeader(formatMessage({ id: 'law-portal.our-laws.header', defaultMessage: 'Our laws' }));
  }, [setHeader, formatMessage]);

  const handleDataFetch = useCallback(
    async (query: Query<AcknowledgedChange>) => {
      if (!isFiltersInitialized) {
        return { data: [], page: 0, totalCount: 0 };
      }
      const result = await api.searchlawAcknowledgedChanges<Omit<AcknowledgedChange, 'tableRowId'>>({
        orderDirection: query.orderDirection !== 'asc' ? OrderDirection.Descending : OrderDirection.Ascending,
        orderBy: query.orderBy !== undefined ? query.orderBy.field : 'acknowledgedAt',
        page: query.page,
        pageSize: query.pageSize,
        search: query.search,
        filters: filters,
      } as IGridQueryRequest);

      return {
        ...result,
        data: result.data.map((row, index) => ({
          ...row,
          tableRowId: `${row.type}-${row.lawId ?? row.lawReqId}-${row.id}-${row.userId}-${index}`,
        })),
      };
    },
    [api, filters, isFiltersInitialized],
  );

  const renderDate = (date?: Date) => {
    return date ? moment(date).format('ll') : undefined;
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
useEffect(() => {
  const updatedQueryParams = Object.keys(filters)
    .filter(key => {
      const value = filters[key];
      if (Array.isArray(value)) {
        return value.join() !== "";
      } else if (typeof value === 'object' && value !== null) {
        return value.min !== "" && value.max !== "";
      }
      return false;
    })
    .map(key => {
      const value = filters[key];
      if (Array.isArray(value)) {
        return `${key}=${encodeURIComponent(value.join())}`;
      } else if (typeof value === 'object' && value !== null) {
        return `${key}[min]=${encodeURIComponent(value.min)}&${key}[max]=${encodeURIComponent(value.max)}`;
      }
      return ''; // Fallback return to satisfy ESLint
    })
    .filter(param => param !== '') // Remove any empty strings
    .join('&');

  navigate({
    pathname: location.pathname,
    search: `?${updatedQueryParams}`,
  });
}, [filters, navigate, location.pathname]);

  const handleExport = async () => {
    const response = await api.exportlawAcknowledgedChanges();
    const blob = await response?.blob();
    const contentDisposition = response.headers.get('Content-Disposition');
    let fileName = 'LawsExport.xlsx';
    if (contentDisposition && contentDisposition.includes('attachment')) {
      const matches = /filename=([^;]*)/.exec(contentDisposition);
      if (matches && matches[1]) {
        fileName = matches[1];
      }
    }
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
  };
  
  const getLawId = (row: AcknowledgedChange) => row.lawId ?? row.lawReqId ?? (row as any).LawId ?? (row as any).LawReqId;
  const refreshTable = () => tableRef.current?.onQueryChange();
  const handleUpdateAcknowledgement = async (
    target: ChangeAcknowledgeDialogTarget,
    comment: string,
    affect?: boolean,
  ) => {
    if (target.lawId) {
      await api.acknowledgeLawPortalLawChange(target.lawId, target.id, comment, affect);
    }
  };

  return (
    <>
      <ChangeFilter filters={filters} setFilter={setFilter} />
      <hr style={{ marginTop: '25px', marginBottom: '25px' }} />
      <MTable
        tableRef={tableRef}
        columns={[
          {
            title: formatMessage({ id: 'law-portal.our-laws.table.law' }),
            field: 'name',
          },
          {
            title: formatMessage({ id: 'law-portal.statistics.table.number' }),
            field: 'number',
            render: (row) => {
              return (
                <MuiLink component="button" onClick={(event) => {
                  event.stopPropagation();
                  openChangeDialog(row, 'review');
                }} style={{ color: '#0000EE', font: 'inherit', textAlign: 'left', textDecoration: 'underline' }}>
                  {row.number}
                </MuiLink>
              );
            },
          },
          {
            title: formatMessage({ id: 'law-portal.statistics.table.effectiveFrom' }),
            field: 'effectiveFrom',
            render: (row) => renderDate(row.effectiveFrom),
          },
          {
            title: formatMessage({ id: 'law-portal.statistics.table.user' }),
            field: 'userName',
          },
          {
            title: formatMessage({ id: 'law-portal.statistics.table.acknowledgedAt' }),
            field: 'acknowledgedAt',
            render: (row) => renderDate(row.acknowledgedAt),
          },
          {
            title: formatMessage({ id: 'law-portal.statistics.table.affectbussiness' }),
            field: 'affectsBusiness',
            render: (row: any) => row?.affectsBusiness == null ? "-" : row?.affectsBusiness ? formatMessage({ id: "law-portal.our-law.ack.latest-change.checkbox.text.affect" }) : formatMessage({ id: "law-portal.our-law.ack.latest-change.checkbox.text.not_affect" }),
          },
          {
            title: formatMessage({ id: 'law-portal.statistics.table.comment' }),
            field: 'comment',
            render: (row) => (row.comment ? row.comment : ''),
          },
        ]}
        data={handleDataFetch}
        onRowClick={(_, row) => row && openChangeDialog(row, 'review')}
        options={{ idSynonym: 'tableRowId' }}
        actions={[
          (row) => ({
            icon: () => <Edit />,
            tooltip: formatMessage({ id: 'component.mtable.editTooltip' }),
            onClick: () => openChangeDialog(row, 'edit'),
            hidden: Number(row.userId) !== Number(user.id),
          }),
        ]}
        components={{
          Toolbar: (props) => (
            <div className={classes.toolbar}>
              <MTableToolbar {...props} />
              <SaveAlt style={{ cursor: 'pointer' }} onClick={() => handleExport()} />
            </div>
          ),
        }}
      />
      {showScrollTop && (
        <Tooltip title={formatMessage({ id: "bottom.to.top.btn.tooltip.text", defaultMessage: "Back to top" })}>
          <Button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className={classes.topButton}
          >
            <ArrowUpward style={{ fontSize: 20 }} />
          </Button>
        </Tooltip>
      )}
      {editingAcknowledgement && (
        <ChangeAcknowledgeDialog
          target={{
            id: editingAcknowledgement.id,
            number: editingAcknowledgement.number,
            name: editingAcknowledgement.name,
            effectiveFrom: editingAcknowledgement.effectiveFrom,
            userId: editingAcknowledgement.userId,
            lawId: getLawId(editingAcknowledgement),
            mode: editingAcknowledgement.mode,
            acknowledgement: {
              comment: editingAcknowledgement.comment,
              affectsBusiness: editingAcknowledgement.affectsBusiness,
              acknowledgedAt: editingAcknowledgement.acknowledgedAt,
              userName: editingAcknowledgement.userName,
            },
          }}
          onClose={() => setEditingAcknowledgement(undefined)}
          onAcknowledged={refreshTable}
          onAcknowledge={handleUpdateAcknowledgement}
        />
      )}
    </>
  );
};
